import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {  Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';

import { finalize } from 'rxjs';

import { TopNavbarService } from '@shared/services/topNavbar/topNavbar.service';
import { IUserProfile } from '@app/features/profile/interfaces/profile.interface';
import { ICountries, ITimeZone } from '@shared/interfaces/app.interface';
import { ToastService } from '@shared/services/toaster/toaster.service';
import { ImageUpload } from '@shared/components/image-upload/image-upload';
import { ToastOrConfirmMessages } from '@common/app.constant';
import { UserService } from '@app/features/profile/services/user.service';
import { Spinner } from '@shared/components/spinner/spinner';
import { Utility } from '@common/utility';
import { getTimeZoneLabel } from '@common/timezone.util';

@Component({
  selector: 'app-profile',
  imports: [
    NgxIntlTelInputModule,
    ReactiveFormsModule,
    NgSelectModule,
    CommonModule,
    FormsModule,
    ImageUpload,
    Spinner,
  ],
  providers: [TitleCasePipe],
  templateUrl: './profile.html',
  styleUrl: './profile.scss'
})
export class Profile implements OnInit {
  gender = signal([{ key: 'male', name: 'Male' }, { key: 'female', name: 'Female' }]);
  coverPreview = signal<string | ArrayBuffer | null>(null);
  preview = signal<string | ArrayBuffer | null>(null);
  preferredCountries: string[] = ['us', 'pk', 'gb'];
  SearchCountryField = SearchCountryField;
  timeZoneLoader = signal<boolean>(false);
  PhoneNumberFormat = PhoneNumberFormat;
  isLoading = signal<boolean>(false);
  isEdit = signal<boolean>(false);
  profileLoader = signal(false);
  coverLoader = signal(false);
  CountryISO = CountryISO;
  separateDialCode = true;
  locations = signal([])
  profileForm: FormGroup
  timeZones = signal([]);
  constructor(
    private topNavbar: TopNavbarService,
    private titleCasePipe: TitleCasePipe,
    private formBuilder: FormBuilder,
    public userService: UserService,
    private toaster: ToastService,
    public utility: Utility,
  ) {
    this.topNavbar.topNavbar.set(true);
    this.profileForm = this.getInitForm()
  }
  user = computed(() => this.userService.userDetail());
  timeZoneLabel = computed(() => getTimeZoneLabel(this.user()?.timezone));
  ngOnInit(): void {
    //  this.getFormData()
    this.getLocations()
  }
  fullName = computed(() => {
    return `${this.userService.userDetail()?.first_name} ${this.userService.userDetail()?.last_name}`
  })
  onFileSelected(event: any, type: string) {
    const file = (event.target as HTMLInputElement).files?.[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const reader = new FileReader();
    reader.onload = () => {
      if (type === 'cover_photo') {
        this.coverLoader.set(true);
      } else {
        this.profileLoader.set(true);
      }
      this.userService.uploadProfileOrCoverPhoto(formData).pipe(finalize(() => {
        this.coverLoader.set(false)
        this.profileLoader.set(false)
      })).subscribe({
        next: ((res: { success: boolean, data: IUserProfile }) => {
          if (res.success === true) {
            this.userService.setUserDetail(res.data);
            this.toaster.show('Image Uploaded successfully');
          }
        }),
        error: (error) => {
          this.toaster.show(error.error?.errors.file[1], { type: 'error' })
        }
      })
    };
    reader.readAsDataURL(file);

  }
  removeImage(type: string): void {
    const data = { type: type }
    this.userService.deleteProfileOrCoverPhoto(data).subscribe({
      next: ((res: { success: boolean, data: IUserProfile }) => {
        if (res.success === true) {
          this.userService.setUserDetail(res.data)
        }
      })
    })
  }
  uploadProfileImage(file , type : string){
    //  const file = (data.target as HTMLInputElement).files?.[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
      if (type === 'cover_photo') {
        this.coverLoader.set(true);
      } else {
        this.profileLoader.set(true);
      }
      this.userService.uploadProfileOrCoverPhoto(formData).pipe(finalize(() => {
        this.coverLoader.set(false)
        this.profileLoader.set(false)
      })).subscribe({
        next: ((res: { success: boolean, data: IUserProfile }) => {
          if (res.success === true) {
            this.userService.setUserDetail(res.data);
            this.toaster.show('Image Uploaded successfully');
          }
        }),
        error: (error) => {
          this.toaster.show(error.error?.errors.file[1], { type: 'error' })
        }
      })
    // reader.readAsDataURL(file);
  }
  getInitForm(): FormGroup {
    return this.formBuilder.group({
      fullName: ['', [Validators.required]],
      contactNumber: [undefined],
      designation: [''],
      department: [''],
      gender: [null],
      location: [null],
      timeZone: [null]

    })
  }
  showEditForm(): void {
    this.isEdit.set(true)
    setTimeout(()=> {
      this.getFormData()
    })
  }
  onCancelClick(): void {
    this.isEdit.set(false);
    this.profileForm.reset()
  }
  getFormData(): void {
    const user = this.userService.userDetail()
    this.onLocationChange(user?.location, () => {
      const matchedTz = this.timeZones().find(tz => tz.id === user?.timezone?.id);
      this.profileForm.patchValue({ timeZone: matchedTz?.id });
    });
    this.profileForm.patchValue({
      fullName: `${user?.first_name} ${user?.last_name}`,
      contactNumber: {
        number: user.phone_number,
        // internationalNumber: `${user.dial_code} ${user.phone_number}`,
        nationalNumber: user.phone_number,
        // e164Number: `${user.dial_code}${user.phone_number}`,
        countryCode: user.country_code as CountryISO,
      },
      designation: user?.designation,
      department: user?.department,
      gender: this.titleCasePipe.transform(user?.gender),
      location: this.locations().find((location: ICountries) => location.id === user?.location?.id)?.id,
    });
  }
  getLocations(): void {
    this.userService.getLocation().subscribe({
      next: ((res: { data: ICountries[] }) => {
        this.locations.set(res?.data)
      })
    })
  }
  onLocationChange(data: ICountries, callback?: () => void): void {
    if (!data) {
      this.timeZones.set([]);
        this.profileForm.get('timeZone').reset();
      return;
    }
    this.timeZoneLoader.set(true)
    this.userService.getTimezones(data?.id).pipe(finalize(() => { this.timeZoneLoader.set(false) })).subscribe({
      next: ((res: { data: ITimeZone[] }) => {
        const tzList = res?.data?.map((tz: ITimeZone) => ({
          id: tz.id,
          name: `${tz.timezone}`,
        }));
        this.timeZones.set(tzList);
        this.profileForm.get('timeZone').reset();
        callback?.();
      })
    })
  }
  onSubmit(): void {
    const [firstName, ...lastName] = this.profileForm.get('fullName')?.value.trim().split(' ');
    const formData = this.utility.toFormData({
      first_name: firstName,
      last_name: lastName.join(' '),
      designation: this.profileForm.get('designation')?.value ?? '',
      phone_number: this.profileForm.get('contactNumber')?.value?.number ?? '',
      country_code: this.profileForm.get('contactNumber')?.value?.countryCode?.toLowerCase() ?? '',
      dial_code: this.profileForm.get('contactNumber')?.value?.dialCode ?? '',
      department: this.profileForm.get('department')?.value ?? '',
      gender: this.profileForm.get('gender')?.value?.toLowerCase() ?? '',
      location_country_id: this.profileForm.get('location')?.value ?? '',
      location_country_timezone_id: this.profileForm.get('timeZone')?.value ?? '',
    });
    if (this.profileForm.valid) {
      this.isLoading.set(true)
      this.userService.updateUserProfile(formData).pipe(finalize(() => { this.isLoading.set(false) })).subscribe({
        next: ((res: { success: boolean, data: IUserProfile }) => {
          if (res.success === true) {
            this.userService.setUserDetail(res.data);
            this.toaster.show(ToastOrConfirmMessages.userProfile.Update)
            this.isEdit.set(false);
          }
        })
      })
    }

  }
  get phoneControl() {
    return this.profileForm.get('contactNumber');
  }
}
